import * as React from 'react';
import { useCallback } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store/hooks';
import { RQAPICall } from 'store/APIClient';
import { useMutation } from '@tanstack/react-query';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { capitalize } from 'helpers/helpers';
import Loading from 'components/Loading';
import { SnakifiedIntegrationT } from 'routes/IntegrationsList/types';
import './_cellar.scss';
import ParameterInput from './ParameterInputs/ParameterInput';
import ParameterExampleValue from './ParameterExampleValue';

const DEBOUNCE_INTERVAL = 1000;

type Props = {
    integration: SnakifiedIntegrationT;
    isIntegrationLoading: boolean;
};

export const IntegrationParameterManager = ({
    integration,
    isIntegrationLoading,
}: Props): React.ReactElement => {
    const { parameters, parameters_type: parametersType } = integration;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const integrationMutation = useMutation({
        // @ts-expect-error [TS migration] Was not detected by flow
        mutationFn: async ({ value, parameterName }) => {
            const body = { parameters: {} };
            // @ts-expect-error [TS migration] Was not detected by flow
            body.parameters[parameterName] = value;

            const response = await RQAPICall({
                endpoint: `integrations/${integration.id}`,
                method: 'PUT',
                body,
                skipCaseConversion: true,
            });
            return response;
        },
        onSuccess: (data) => {
            dispatch.vision.notifyUser({
                message: data.content.message,
                snackType: 'success',
            });
        },
        onError: (error) => {
            dispatch.vision.notifyUser({
                message: t(error.message),
                snackType: 'error',
            });
        },
    });

    const debounceIntegrationMutation = useCallback(
        debounce(
            ({ parameterName, value }) =>
                // @ts-expect-error [TS migration] Was not detected by flow
                integrationMutation.mutate({ parameterName, value }),
            DEBOUNCE_INTERVAL,
        ),
        [],
    );

    // @ts-expect-error [TS migration] Was not detected by flow
    const setParameterValue = (parameterName, value) => {
        parameters[parameterName] = value;
        debounceIntegrationMutation({ parameterName, value });
    };

    if (isIntegrationLoading) return <Loading />;

    return (
        <div className="integration-parameters-container">
            <div className="parameters-header">
                <Typography variant="h4">
                    {`${t('cellar.parameterList')} ${capitalize(
                        t(`mappings.${integration.software.toLowerCase()}`),
                    )} - ${integration.slug.toLowerCase()}`}
                </Typography>

                <Typography color="textSecondary" className="comment">
                    {t('cellar.parameterListComment')}
                </Typography>
            </div>
            <div className="parameters-container">
                <List component="div" disablePadding dense>
                    <ListItem className="parameter-row">
                        <ListItemText
                            className="parameter-name column-name"
                            inset
                            primary={
                                <Typography variant="h5">
                                    {t('cellar.columnNameParameter')}
                                </Typography>
                            }
                        />
                        <ListItemText
                            className="parameter-value column-name"
                            inset
                            primary={
                                <Typography variant="h5">
                                    {t('cellar.columnNameValue')}
                                </Typography>
                            }
                        />
                        <ListItemText
                            className="parameter-example column-name"
                            inset
                            primary={
                                <Typography variant="h5">
                                    {t('cellar.columnNameExample')}
                                </Typography>
                            }
                        />
                    </ListItem>
                    {Object.keys(parameters).map((parameterName) => {
                        const value = parameters[parameterName];
                        const parameterType = parametersType[parameterName];
                        return (
                            <ListItem
                                className="parameter-row"
                                key={parameterName}
                            >
                                <ListItemText
                                    inset
                                    className="parameter-name"
                                    primary={
                                        <Tooltip
                                            title={parameterName as string}
                                        >
                                            <Typography
                                                variant="h5"
                                                color="primary"
                                            >
                                                {parameterName as string}
                                            </Typography>
                                        </Tooltip>
                                    }
                                />
                                <ParameterInput
                                    parameterName={parameterName as string}
                                    parameterType={parameterType}
                                    value={value}
                                    setParameterValue={setParameterValue}
                                />
                                <ListItemText
                                    inset
                                    className="parameter-example"
                                    primary={
                                        <ParameterExampleValue
                                            parameterType={parameterType}
                                        />
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </div>
        </div>
    );
};
